import { Modal, Input as AntInput } from 'antd';
import { type FC, useState } from 'react';
import { useCreateForumQuestion } from '../../../../services/forumQuestion';
import { IconMessagePlus } from '@tabler/icons-react';
import clsx from 'clsx';
import { useTheme } from '../../../../contexts/ThemeContext';
import './ForumQuestionModal.scss';

interface Props {
  onClose: () => void
  open: boolean
  refetch: () => Promise<any>
}

const ForumQuestionModal: FC<Props> = ({ refetch, open, onClose }) => {
  const { theme } = useTheme();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const { createForumQuestion, loading: createLoading } = useCreateForumQuestion();

  const handleClose = () => {
    onClose();
    setTitle('');
    setContent('');
  };

  const handleSave = async () => {
    await createForumQuestion({
      title,
      content
    });
    await refetch();
    handleClose();
  };

  return (
    <Modal
      title={
        <div className={clsx(
          'flex items-center gap-3',
          'text-gray-900 dark:text-white'
        )}>
          <div className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            'bg-gradient-to-br from-blue-500 to-purple-500',
            'dark:from-blue-600 dark:to-purple-600'
          )}>
            <IconMessagePlus size={20} className="text-white" />
          </div>
          <span className="text-xl font-bold">Añadir tema</span>
        </div>
      }
      open={open}
      onOk={handleSave}
      onCancel={handleClose}
      okText="Publicar"
      okButtonProps={{
        disabled: !title.trim() || !content.trim(),
        loading: createLoading,
        className: clsx(
          'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
          'border-none shadow-md hover:shadow-lg',
          'transition-all duration-300'
        )
      }}
      cancelButtonProps={{
        className: clsx(
          'border-gray-300 dark:border-gray-600',
          'text-gray-700 dark:text-gray-300',
          'hover:border-gray-400 dark:hover:border-gray-500',
          'transition-all duration-300'
        )
      }}
      width={600}
      className={clsx(
        'forum-question-modal',
        theme === 'dark' ? 'dark-modal' : 'light-modal'
      )}
      style={{
        borderRadius: '16px',
        overflow: 'hidden'
      }}
      maskStyle={{
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
      }}
      wrapClassName={clsx(
        'forum-question-modal-wrapper',
        theme === 'dark' ? 'dark-modal-wrapper' : 'light-modal-wrapper',
        'bottom-sheet-mobile'
      )}
      centered={false}
    >
      <div className={clsx(
        'flex flex-col space-y-4',
        'w-full'
      )}>
        <div className="flex flex-col space-y-2 w-full">
          <label className={clsx(
            'text-sm font-medium transition-colors duration-300',
            'text-gray-700 dark:text-gray-300'
          )}>
            Título
          </label>
          <AntInput
            value={title}
            placeholder="Escribe el título del tema..."
            onChange={(e) => setTitle(e.target.value)}
            className={clsx(
              'w-full rounded-lg',
              '[&_.ant-input]:bg-white',
              '[&_.ant-input]:dark:bg-surface-dark',
              '[&_.ant-input]:border-gray-300',
              '[&_.ant-input]:dark:border-borders',
              '[&_.ant-input]:text-gray-900',
              '[&_.ant-input]:dark:text-white',
              '[&_.ant-input]:placeholder:text-gray-400',
              '[&_.ant-input]:dark:placeholder:text-gray-500',
              '[&_.ant-input]:focus:border-blue-500',
              '[&_.ant-input]:dark:focus:border-blue-400',
              '[&_.ant-input]:focus:shadow-md',
              '[&_.ant-input]:dark:focus:shadow-blue-500/20'
            )}
          />
        </div>
        <div className="flex flex-col space-y-2 w-full">
          <label className={clsx(
            'text-sm font-medium transition-colors duration-300',
            'text-gray-700 dark:text-gray-300'
          )}>
            Contenido
          </label>
          <AntInput.TextArea
            value={content}
            placeholder="Describe tu pregunta o tema de discusión..."
            onChange={(e) => setContent(e.target.value)}
            rows={6}
            className={clsx(
              'w-full rounded-lg',
              '[&_.ant-input]:bg-white',
              '[&_.ant-input]:dark:bg-surface-dark',
              '[&_.ant-input]:border-gray-300',
              '[&_.ant-input]:dark:border-borders',
              '[&_.ant-input]:text-gray-900',
              '[&_.ant-input]:dark:text-white',
              '[&_.ant-input]:placeholder:text-gray-400',
              '[&_.ant-input]:dark:placeholder:text-gray-500',
              '[&_.ant-input]:focus:border-blue-500',
              '[&_.ant-input]:dark:focus:border-blue-400',
              '[&_.ant-input]:focus:shadow-md',
              '[&_.ant-input]:dark:focus:shadow-blue-500/20'
            )}
          />
        </div>
      </div>
    </Modal>
  );
};

export { ForumQuestionModal };
